<template>
  <el-form-item :label="label" :prop="propName">
    <div v-if="files.length" class="multi-file-list">
      <div v-for="(f, i) in files" :key="i" class="multi-file-item">
        <div class="multi-file-preview-box">
          <img
            v-if="isImage(f)"
            class="multi-file-preview"
            :src="f.file_url"
            alt=""
          />
          <span v-else class="multi-file-preview multi-file-icon">{{ extOf(f) || 'FILE' }}</span>
          <div v-show="!isUploading" class="multi-file-close-box pointer danger-color-hover" @click="remove(i)">
            <v-icon name="close"/>
          </div>
          <div v-if="uploadingIndex === i" class="multi-file-uploading">上传中…</div>
        </div>
        <div class="multi-file-name">备注图 {{ i + 1 }}</div>
      </div>
    </div>

    <el-upload
      v-if="!disabled && files.length < maxCount"
      drag
      action=""
      :show-file-list="false"
      :auto-upload="false"
      :multiple="true"
      accept="image/*"
      :class="disabled ? 'disabled' : ''"
      :on-change="handleChange"
    >
      <i class="el-icon-upload"/>
      <div class="el-upload__text">
        将{{ label }}拖至此处，或 <em>点击上传</em>
      </div>
    </el-upload>

    <div class="el-upload__tip">
      大小不可超过 <span class="primary-color">{{ humanizeMaxSize }}</span>，最多可上传
      <span class="primary-color">{{ maxCount }} 个</span><span v-if="placeholder">，{{ placeholder }}</span>
    </div>

    <el-alert
      v-if="alert"
      :title="alert"
      type="warning"
      show-icon
      :closable="false"
    />
  </el-form-item>
</template>

<script>
import { putImage, imageCdnHost } from '@/api/oss';

export default {
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    prefix: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '备注文件',
    },
    propName: {
      type: String,
      default: 'remark_files',
    },
    maxCount: {
      type: Number,
      default: 5,
    },
    maxSize: {
      type: Number,
      default: 10485760,// 10 MB
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    alert: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    files: [],
    uploadingIndex: -1,
  }),
  computed: {
    humanizeMaxSize() {
      return this.getHumanizedSize(this.maxSize);
    },
    isUploading() {
      return this.uploadingIndex > -1;
    },
  },
  watch: {
    value(val) {
      this.syncFiles(val);
    },
  },
  beforeMount() {
    this.syncFiles(this.value);
  },
  methods: {
    syncFiles(val) {
      const list = Array.isArray(val) ? val : [];
      // 兼容历史单 URL（字符串）数据
      this.files = list.map((f) => (typeof f === 'string' ? ({ file_url: f, file_name: '', file_size: 0, file_ext: '' }) : { ...f }));
    },
    handleChange(file) {
      if (!file || !file.raw) return;
      this.upload(file.raw);
    },
    async upload(file) {
      if (this.files.length >= this.maxCount) {
        return this.$message.warning(`备注文件最多上传 ${this.maxCount} 个！`);
      }
      if (!file.type || !file.type.startsWith('image/')) {
        return this.$message.error('仅支持上传 jpg/png/gif/webp 图片！');
      }
      if (file.size > this.maxSize) {
        return this.$message.error(`单个文件大小不可超过 ${this.humanizeMaxSize}`);
      }

      const index = this.files.length;
      this.uploadingIndex = index;
      const objectKey = await putImage({
        bucket_prefix: this.prefix,
        imageData: file,
      });
      this.uploadingIndex = -1;
      if (!objectKey) return;

      this.files.push({
        file_url: `${imageCdnHost}${objectKey}`,
        file_name: file.name,
        file_ext: (file.name.split('.').pop() || '').toLowerCase(),
        file_size: file.size,
        mime_type: file.type,
      });
      this.$message.success('已完成上传！');
      this.emitChange();
    },
    remove(index) {
      // 只移除引用，不删除 OSS 文件（与项目全局策略一致）
      this.files.splice(index, 1);
      this.emitChange();
    },
    emitChange() {
      this.$emit('input', this.files.map((f) => ({ ...f })));
    },
    isImage(f) {
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(this.extOf(f).toLowerCase());
    },
    extOf(f) {
      return f.file_ext || (f.file_name ? (f.file_name.split('.').pop() || '') : '');
    },
    getHumanizedSize(number) {
      if (number < 1024) return `${number} B`;
      if (number < 1048576) {
        const kb = number / 1024;
        return `${Number.isInteger(kb) ? kb : kb.toFixed(1)} KB`;
      }
      if (number < 1073741824) {
        const mb = number / 1048576;
        return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`;
      }
      const gb = number / 1073741824;
      return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
    },
  },
};
</script>

<style src="./index.css" scoped/>
